/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * "Request cut-over": POST /cutover_requests. The change window is picked in the browser's time
 * zone and sent as UTC; the deployment options are the existing deployment form (embedded), plus
 * how long the deployment can be rolled back. Someone other than the requester approves it on the
 * Change control page, and Swift starts the deployment inside the window.
 */

import * as React from "react";
import styled from "styled-components";
import { CalendarClock, ClipboardCheck, LoaderCircle, Send } from "lucide-react";

import DeploymentOptions from "@src/components/modules/TransferModule/DeploymentOptions";
import { ActionButton, DetailsTabs } from "@src/components/ui/DetailsKit";
import { T9 } from "@src/components/ui/Lists/ListTable";
import {
  CheckRow,
  Chip,
  FieldHint,
  FootNote,
  FormField,
  FormGrid,
  FormSection,
  Notice,
  OpsDialog,
  OpsInput,
  OpsTextarea,
  SectionHeading,
} from "@src/components/ui/OpsDialog";
import cutoverSource, { apiErrorMessage, buildDeploymentOptions } from "@src/sources/CutoverSource";
import notificationStore from "@src/stores/NotificationStore";

import type { CutoverRequest } from "@src/@types/Cutover";
import type { Field } from "@src/@types/Field";
import type { Instance, InstanceScript } from "@src/@types/Instance";
import type { TransferItemDetails } from "@src/@types/MainItem";
import type { MinionPool } from "@src/@types/MinionPool";

import {
  formatDuration,
  formatUtc,
  fromLocalInput,
  localOffsetLabel,
  localZone,
  shortInstance,
  toApiUtc,
  toLocalInput,
  validateWindow,
  windowPresets,
} from "./cutoverUtils";

const MAX_REASON = 4000;
export const DEFAULT_RETENTION_HOURS = 72;

const InstanceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 168px;
  overflow: auto;
  padding: 10px 12px;
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
`;
const Presets = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const UtcLine = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
  font-variant-numeric: tabular-nums;
  b {
    color: ${T9.slate700};
    font-weight: 500;
  }
`;
const OptionsFrame = styled.div`
  margin: -4px -24px 0 -24px;
  padding: 0 24px;
`;

type DeploymentFormValue = {
  fields: Field[];
  uploadedUserScripts: InstanceScript[];
  removedUserScripts: InstanceScript[];
  minionPoolMappings: { [instance: string]: string };
};

type Props = {
  transfer: TransferItemDetails;
  instancesDetails: Instance[];
  loadingInstances?: boolean;
  minionPools: MinionPool[];
  onClose: () => void;
  onCreated?: (request: CutoverRequest) => void;
  /** For tests. */
  now?: Date;
};

const RequestCutoverDialog = (props: Props) => {
  const { transfer } = props;
  const now = props.now || new Date();
  const presets = React.useMemo(() => windowPresets(now), []);
  const [tab, setTab] = React.useState("change");
  const [instances, setInstances] = React.useState<string[]>(transfer.instances || []);
  const [start, setStart] = React.useState(toLocalInput(presets[0].start));
  const [end, setEnd] = React.useState(toLocalInput(presets[0].end));
  const [reason, setReason] = React.useState("");
  const [retention, setRetention] = React.useState(String(DEFAULT_RETENTION_HOURS));
  const [deployment, setDeployment] = React.useState<DeploymentFormValue | null>(null);
  const [touched, setTouched] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const startDate = fromLocalInput(start);
  const endDate = fromLocalInput(end);
  const windowErrors = validateWindow(startDate, endDate, now.getTime());
  const retentionNumber = retention.trim() === "" ? null : Number(retention);
  const retentionError =
    retentionNumber !== null && (!Number.isInteger(retentionNumber) || retentionNumber < 0)
      ? "Use a whole number of hours (0 or more)."
      : null;
  const instancesError = instances.length ? null : "Choose at least one VM.";
  const reasonError = reason.length > MAX_REASON ? `At most ${MAX_REASON} characters.` : null;
  const invalid = Boolean(windowErrors.start || windowErrors.end || retentionError || instancesError || reasonError);
  const alreadyOpen = startDate && endDate && startDate.getTime() <= now.getTime() && endDate.getTime() > now.getTime();

  const submit = async () => {
    setTouched(true);
    if (invalid || !startDate || !endDate) {
      setTab("change");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const all = instances.length === (transfer.instances || []).length;
      const request = await cutoverSource.create({
        transfer_id: transfer.id,
        window_start: toApiUtc(startDate),
        window_end: toApiUtc(endDate),
        reason: reason.trim(),
        instances: all ? null : instances,
        deployment_options: buildDeploymentOptions({
          fields: deployment?.fields || [],
          uploadedUserScripts: deployment?.uploadedUserScripts || [],
          removedUserScripts: deployment?.removedUserScripts || [],
          userScriptData: transfer.user_scripts,
          minionPoolMappings: deployment?.minionPoolMappings || {},
          rollbackRetentionHours: retentionNumber,
        }),
      });
      notificationStore.alert("Cut-over requested. It waits for someone else to approve it.", "success");
      props.onCreated?.(request);
      props.onClose();
    } catch (err: any) {
      setError(apiErrorMessage(err, "The cut-over request could not be created."));
    } finally {
      setSaving(false);
    }
  };

  const toggleInstance = (name: string, checked: boolean) =>
    setInstances(prev => (checked ? (transfer.instances || []).filter(i => i === name || prev.includes(i)) : prev.filter(i => i !== name)));

  const renderChange = () => (
    <>
      <FormSection>
        <SectionHeading>Change window</SectionHeading>
        <Presets>
          {presets.map(p => (
            <Chip
              key={p.id}
              type="button"
              selected={start === toLocalInput(p.start) && end === toLocalInput(p.end)}
              onClick={() => {
                setStart(toLocalInput(p.start));
                setEnd(toLocalInput(p.end));
              }}
            >
              {p.label}
            </Chip>
          ))}
        </Presets>
        <FormGrid>
          <FormField label="Opens" required error={touched || start ? windowErrors.start : null}>
            <OpsInput
              type="datetime-local"
              value={start}
              invalid={Boolean(windowErrors.start)}
              onChange={e => setStart(e.target.value)}
              data-test-id="cutover-start"
            />
          </FormField>
          <FormField label="Closes" required error={windowErrors.end}>
            <OpsInput
              type="datetime-local"
              value={end}
              min={start || undefined}
              invalid={Boolean(windowErrors.end)}
              onChange={e => setEnd(e.target.value)}
              data-test-id="cutover-end"
            />
          </FormField>
        </FormGrid>
        <UtcLine>
          Times are in {localZone()} ({localOffsetLabel(startDate || now)}).
          {startDate && endDate && endDate > startDate ? (
            <>
              {" "}
              Sent as <b>{formatUtc(startDate)}</b> to <b>{formatUtc(endDate)}</b> · a{" "}
              {formatDuration(endDate.getTime() - startDate.getTime())} window.
            </>
          ) : null}
        </UtcLine>
        {alreadyOpen ? (
          <Notice tone="info" icon={CalendarClock}>
            The window is already open: SwiftIQ starts the deployment as soon as the request is approved.
          </Notice>
        ) : null}
        <FieldHint>The window bounds when the deployment can start; a deployment may run past the window's end.</FieldHint>
      </FormSection>

      <FormSection>
        <SectionHeading>Scope</SectionHeading>
        <FormField
          as="div"
          label="VMs to cut over"
          aside={`${instances.length} of ${(transfer.instances || []).length}`}
          error={touched ? instancesError : null}
        >
          <InstanceList>
            {(transfer.instances || []).length > 1 ? (
              <CheckRow
                checked={instances.length === (transfer.instances || []).length}
                onChange={checked => setInstances(checked ? [...(transfer.instances || [])] : [])}
                label="All VMs of the transfer"
              />
            ) : null}
            {(transfer.instances || []).map(name => (
              <CheckRow
                key={name}
                checked={instances.includes(name)}
                onChange={checked => toggleInstance(name, checked)}
                label={shortInstance(name)}
                description={name !== shortInstance(name) ? name : undefined}
              />
            ))}
          </InstanceList>
        </FormField>
      </FormSection>

      <FormSection>
        <SectionHeading>Change record</SectionHeading>
        <FormField
          label="Reason or change reference"
          aside={`${reason.length} / ${MAX_REASON}`}
          error={reasonError}
          hint="Shown to the approver and kept in the audit history, e.g. CHG-20431: cut over the ERP web tier."
        >
          <OpsTextarea
            value={reason}
            maxLength={MAX_REASON + 100}
            placeholder="CHG-20431: cut over the ERP web tier"
            onChange={e => setReason(e.target.value)}
            data-test-id="cutover-reason"
          />
        </FormField>
        <FormGrid>
          <FormField
            label="Rollback available for"
            error={retentionError}
            hint="Hours after the deployment completes during which it can be rolled back. 0 turns rollback off."
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <OpsInput
                type="number"
                min={0}
                step={1}
                value={retention}
                invalid={Boolean(retentionError)}
                style={{ width: 120 }}
                onChange={e => setRetention(e.target.value)}
                data-test-id="cutover-retention"
              />
              <span style={{ fontSize: 13, color: T9.slate500 }}>hours</span>
            </div>
          </FormField>
        </FormGrid>
      </FormSection>
    </>
  );

  return (
    <OpsDialog
      title="Request cut-over"
      subtitle={
        <>
          {transfer.notes || transfer.instances?.[0]} · someone other than you approves it, then SwiftIQ deploys inside the window.
        </>
      }
      icon={ClipboardCheck}
      width={720}
      onRequestClose={props.onClose}
      testId="requestCutoverDialog"
      footer={
        <>
          <FootNote>
            {startDate && endDate && endDate > startDate
              ? `Window: ${formatDuration(endDate.getTime() - startDate.getTime())}, opens ${startDate.toLocaleString("en-GB", {
                  weekday: "short",
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}`
              : "Choose a change window."}
          </FootNote>
          <ActionButton variant="secondary" type="button" onClick={props.onClose} disabled={saving}>
            Cancel
          </ActionButton>
          <ActionButton type="button" onClick={submit} disabled={saving} data-test-id="cutover-submit">
            {saving ? <LoaderCircle size={16} className="spin" /> : <Send size={16} />}
            {saving ? "Requesting…" : "Request cut-over"}
          </ActionButton>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {error ? (
          <Notice tone="danger" title="SwiftIQ refused the request">
            {error}
          </Notice>
        ) : null}
        <DetailsTabs
          items={[
            { label: "Change", value: "change" },
            { label: "Deployment options", value: "options" },
          ]}
          selectedValue={tab}
          onChange={setTab}
        />
        <div style={{ display: tab === "change" ? "block" : "none" }}>{renderChange()}</div>
        <div style={{ display: tab === "options" ? "block" : "none", minHeight: 420 }}>
          <OptionsFrame>
            <FieldHint style={{ display: "block", marginBottom: 16 }}>
              The same options as a direct deployment. Options left unchanged take the transfer's values.
            </FieldHint>
            <DeploymentOptions
              embedded
              transferItem={transfer}
              instances={props.instancesDetails}
              loadingInstances={Boolean(props.loadingInstances)}
              minionPools={props.minionPools}
              onCancelClick={() => {}}
              onDeployClick={() => {}}
              onOptionsChange={setDeployment}
            />
          </OptionsFrame>
        </div>
      </div>
    </OpsDialog>
  );
};

export default RequestCutoverDialog;
